import React from 'react';
import * as consts from 'config/consts';
import { useRouting } from 'hooks/useRouting';
import { formatDateForString, formatNumber } from 'utils/formatter';
import { addSignCommaFormat, commaFormat } from '../new-report/utils/formatter';
import { Side, SideLabel } from '../utils/side';
import { AccountType, AccountTypeLabel } from '../utils/accountType';
import {
  amountToAmountColor,
  isSymbolEmpty as isSymbolEmpty_,
} from '../utils/util';
import {
  isNotExistDetailTypes,
  isQtyHiddenProductType,
  productTypesToStringLabel,
  productTypesToUnit,
} from '../utils/productTypes';

export type OrderitemProps = {
  data: {
    tradeTime: string | null;
    symbol: string;
    symbolName: string;
    company: string;
    accountType: number;
    cashMargin: number;
    tradeType: number;
    side: number;
    qty: number | null;
    price: number | null;
    openPriceUSD: number | null;
    profitOrLoss: number | null;
    profitOrLossUSD: number | null;
    productType: number;
    currencyUnit: string | null;
  };
};

export const NewOrderItem = ({ data }: OrderitemProps): React.ReactElement => {
  const isNotExistDetail = isNotExistDetailTypes(data.productType);
  const isSymbolEmpty = isSymbolEmpty_(data.symbol);

  // MEMO: 『類推中』はMONO-***、『類推完了』はDC-***で返却
  const isDataCooperation = /^MONO/.test(data.symbol);
  const isAnalogyComplete = data.symbol === '' || /^DC/.test(data.symbol);

  const pushRouting = (({ pushRouting }) => (path: string) => () => {
    pushRouting({ path }, true);
  })(useRouting());

  const goToDetail = () => {
    if (
      isSymbolEmpty ||
      isNotExistDetail ||
      isDataCooperation ||
      isAnalogyComplete
    )
      return;
    pushRouting(`/tradedetail/${data.symbol}`)();
  };

  const isForeignProfitOrLoss =
    data.currencyUnit !== null && data.currencyUnit !== 'JPY';

  const disableColor = 'text-bluegray-300';

  const isShowDetail =
    isSymbolEmpty || isNotExistDetail || isDataCooperation || isAnalogyComplete
      ? false
      : true;

  return (
    <>
      <div
        className={`bg-white rounded-lg mb-2 px-4 py-4 ${
          isShowDetail ? 'cursor-pointer' : ''
        }`}
        onClick={goToDetail}>
        <div className="flex">
          <div className="flex-1">
            {isDataCooperation && (
              <div className="flex flex-row items-center mb-2 text-sm text-red-500">
                <div className="icon-alert"></div>
                <div className="ml-1">データ取得中です</div>
              </div>
            )}
            <div className="h-10 flex flex-row items-center">
              <SideLabel side={data.side as Side} />
              <div className="flex flex-col ml-2">
                <AccountTypeLabel
                  accountType={data.accountType as AccountType}
                />
                <div className="flex flex-row items-center mt-1 text-xs">
                  <div
                    className={`ml-1 ${isDataCooperation ? disableColor : ''}`}>
                    {productTypesToStringLabel(
                      data.productType,
                      data.cashMargin
                    )}
                  </div>
                  {!isQtyHiddenProductType(data.productType) ? (
                    <div
                      className={`ml-4 mr-2 ${
                        isDataCooperation ? disableColor : ''
                      }`}>
                      {formatNumber(data.qty, { noValueText: '---' })}
                      {productTypesToUnit(data.productType)}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
              <div className="ml-auto text-gray-500 flex items-start text-sm">
                {data.tradeTime === null
                  ? ''
                  : formatDateForString(data.tradeTime, 'yyyy/MM/dd')}
              </div>
            </div>
            <div className="w-full flex flex-row items-center mt-2">
              <div
                className={`font-bold mr-2 ${
                  isDataCooperation ? disableColor : ''
                }`}>
                <div className="line-clamp-2">{data.symbolName}</div>
              </div>
              {!(
                data.cashMargin === consts.CashMargin.現物 &&
                data.side === consts.Side.買 &&
                data.profitOrLoss === null
              ) && (
                <div
                  className="flex flex-col items-end flex-shrink ml-auto"
                  style={{ whiteSpace: 'nowrap' }}>
                  <div
                    className={`flex font-bold items-baseline flex-row ${
                      isDataCooperation
                        ? disableColor
                        : `${amountToAmountColor(data.profitOrLoss ?? 0)}`
                    }`}>
                    <div className="text-base">
                      {addSignCommaFormat(data.profitOrLoss)}
                    </div>
                    <div className="text-base ml-1">円</div>
                  </div>
                  {isForeignProfitOrLoss && data.profitOrLossUSD ? (
                    <div
                      className={`ml-auto mt-2 whitespace-no-wrap ${
                        isDataCooperation
                          ? disableColor
                          : amountToAmountColor(data.profitOrLossUSD)
                      }`}>
                      (
                      {commaFormat(data.profitOrLossUSD, {
                        numberOfDecimals: 2,
                      })}{' '}
                      {data.currencyUnit ?? ''})
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center ml-2">
            <i
              className={`icon-chevron-right text-2xl text-gray-700 -mr-2 ${
                isShowDetail ? '' : 'opacity-0'
              }`}></i>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewOrderItem;
